/* eslint-disable */
import {
  Box,
  Button,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { MobileTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import React, { useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { AiOutlinePlus } from "react-icons/ai";
import theme from "src/theme";
import { calculateEndTime, durationList } from "../../utils";
import FormControl from "@mui/material/FormControl";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { AddSlotsProps } from "../../types";
import { RiDeleteBinLine } from "react-icons/ri";
import {
  CloseActiveIcon,
  CloseSquareIcon,
  ConfirmActiveIcon,
  ConfirmSquareIcon,
} from "src/icons";

interface AddNewAddSlotsProps {
  handleAddNewSlotToList: (slots: AddSlotsProps[]) => void;
  removeSlotFromSlotList: (id: string | undefined) => void;
  slotsList: Array<AddSlotsProps>;
}

const AddNewSlots = function AddNewSlots(props: AddNewAddSlotsProps) {
  const { slotsList, handleAddNewSlotToList, removeSlotFromSlotList } = props;

  const validationSchema = yup.object().shape({
    startTime: yup
      .string()
      .typeError("Select start time")
      .required("Start time required")
      .test(
        "is-overlapping",
        "The selected time overlaps with an existing slot.",
        function (value) {
          if (!value) return true;
          const startTime = moment(value, "hh:mm A");
          const endTime = startTime
            .clone()
            .add(this.parent.duration, "minutes");
          return !slotsList.some((slot) => {
            if (slot.id === this.parent.id) return false; // Exclude the current slot being edited
            const slotStartTime = moment(slot.startTime, "hh:mm A");
            const slotEndTime = moment(slot.endTime, "hh:mm A");
            return (
              startTime.isBetween(
                slotStartTime,
                slotEndTime,
                undefined,
                "[)"
              ) ||
              endTime.isBetween(slotStartTime, slotEndTime, undefined, "(]")
            );
          });
        }
      )
      .test(
        "is-duplicate",
        "A slot with the same start time already exists.",
        function (value) {
          if (!value) return true;
          return !slotsList.some(
            (slot) => slot.startTime === value && slot.id !== this.parent.id
          );
        }
      ),
    duration: yup
      .string()
      .typeError("Select duration")
      .required("Duration required"),
  });

  const resolver = useYupValidationResolver(validationSchema);

  const methods = useForm<AddSlotsProps>({
    resolver,
    defaultValues: {
      id: undefined,
    },
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const [newSlotClicked, setNewSlotClicked] = useState<Boolean>(false);

  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isConfirmHovered, setIsConfirmHovered] = useState(false);

  const handleMouseEnterClose = () => {
    setIsCloseHovered(true);
  };

  const handleMouseLeaveClose = () => {
    setIsCloseHovered(false);
  };

  const handleMouseEnterConfirm = () => {
    setIsConfirmHovered(true);
  };

  const handleMouseLeaveConfirm = () => {
    setIsConfirmHovered(false);
  };

  const startTime = watch("startTime");
  const duration = watch("duration");

  const handleAddNewSlot = () => {
    reset({ startTime: null, duration: null, id: undefined });
    setNewSlotClicked(true);
  };

  const handleCloseAddNewSlot = () => {
    reset({
      startTime: null,
      duration: null,
      id: undefined,
    });
    setNewSlotClicked(false);
  };

  const onSlotClick = (slot: AddSlotsProps) => {
    const { id, startTime, duration } = slot;
    reset({ id, startTime, duration });
    setNewSlotClicked(true);
  };

  const submitHandler: SubmitHandler<AddSlotsProps> = async (data) => {
    try {
      let formattedData: AddSlotsProps;
      let updatedSlotsList: AddSlotsProps[] = []; // Initialize with an empty array

      if (data.id && data.startTime !== null && data.duration !== null) {
        // Find the record with the same id and replace the other data
        formattedData = {
          ...data,
          endTime: calculateEndTime(data.startTime, data.duration),
        };
        updatedSlotsList = slotsList.map((slot) =>
          slot.id === data.id ? formattedData : slot
        );
      } else if (data.startTime !== null && data.duration !== null) {
        // Add new slot
        formattedData = {
          ...data,
          id: uuidv4(),
          endTime: calculateEndTime(data.startTime, data.duration),
        };
        updatedSlotsList = [...slotsList, formattedData];
      }

      // Ensure that updatedSlotsList has been assigned a value before using it
      if (updatedSlotsList.length > 0) {
        handleAddNewSlotToList(updatedSlotsList);
      }
      reset({
        startTime: null,
        duration: null,
        id: undefined,
      });
      setNewSlotClicked(false);
    } catch (e) {
      console.info("...");
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        {/* <LocalizationProvider dateAdapter={AdapterMoment}>  */}
        <div className="flex gap-3">
          <div className="w-[300px]">
            <Button onClick={handleAddNewSlot} className="mt-5">
              <AiOutlinePlus color={theme.palette.primary.main} /> Add New Slot
            </Button>
            {newSlotClicked && (
              <div className="flex gap-2 my-5 relative">
                <Controller
                  name="startTime"
                  control={methods.control}
                  render={({ field }) => (
                    <FormControl
                      error={Boolean(errors.startTime)}
                      className="w-[170px]"
                    >
                      <MobileTimePicker
                        {...field}
                        label="Start time"
                        value={
                          field.value ? moment(field.value, "hh:mm A") : null
                        }
                        onChange={(newValue) =>
                          field.onChange(moment(newValue).format("hh:mm A"))
                        }
                      />
                      {/* {errors.startTime && (
                        <FormHelperText>
                          {errors.startTime.message}
                        </FormHelperText>
                      )} */}
                    </FormControl>
                  )}
                />
                <FormControl
                  className="w-[130px]"
                  error={Boolean(errors.duration)}
                >
                  <InputLabel id="duration-select-label">duration</InputLabel>
                  <Controller
                    name="duration"
                    control={methods.control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="duration-select-label"
                        id="duration-select"
                        label="Duration"
                      >
                        {durationList.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {/* {errors.duration && (
                    <FormHelperText>{errors.duration.message}</FormHelperText>
                  )} */}
                </FormControl>
                <div className="flex gap-1 absolute -bottom-8 right-0 text-secondary">
                  <Box
                    className="cursor-pointer"
                    onMouseEnter={handleMouseEnterClose}
                    onMouseLeave={handleMouseLeaveClose}
                    onClick={handleCloseAddNewSlot}
                  >
                    {isCloseHovered ? (
                      <CloseActiveIcon size={20} />
                    ) : (
                      <CloseSquareIcon size={20} />
                    )}
                  </Box>
                  <Box
                    className={`cursor-pointer ${
                      (!startTime || !duration) &&
                      "cursor-not-allowed opacity-90"
                    }`}
                    onMouseEnter={handleMouseEnterConfirm}
                    onMouseLeave={handleMouseLeaveConfirm}
                    onClick={() =>
                      !startTime || !duration
                        ? null
                        : handleSubmit(submitHandler)()
                    }
                  >
                    {!startTime || !duration ? (
                      <ConfirmSquareIcon color={"#b1b1b1"} size={20} />
                    ) : isConfirmHovered ? (
                      <ConfirmActiveIcon size={20} />
                    ) : (
                      <ConfirmSquareIcon size={20} />
                    )}
                  </Box>
                </div>
              </div>
            )}
            {errors.startTime && (
              <FormHelperText className="text-error">
                {errors.startTime.message}
              </FormHelperText>
            )}
          </div>
        </div>
        {/* </LocalizationProvider> */}
      </form>
      <div className="flex flex-col gap-1 mt-5">
        {slotsList.map((slot: AddSlotsProps, index: number) => (
          <Button
            key={index}
            onClick={() => onSlotClick(slot)}
            className="w-[300px] group"
          >
            <div className="flex justify-between w-[80%]">
              <div>{slot.startTime}</div>
              <div>
                {
                  durationList.find((item) => item.value === slot.duration)
                    ?.label
                }
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  removeSlotFromSlotList(slot.id);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xl text-secondary hover:text-primary"
              >
                <RiDeleteBinLine />
              </div>
            </div>
          </Button>
        ))}
      </div>
    </FormProvider>
  );
};

AddNewSlots.defaultProps = {};

AddNewSlots.propTypes = {};

export default AddNewSlots;
